import React, { useState, useRef, useEffect } from 'react';
import analyticsEvent from 'services/analytic/analytic-service';
import getBrandLogoUrl from 'utilities/brandLogo/getBrandLogoUrl';
import { DeliveryAndReturnContentItem, DeliveryAndReturnContentKey } from 'types/Product';
import { useSelectedSkuContext } from 'context/SelectedSku/SelectedSkuContext';
import { VDPService } from 'services/VDP/VDP-service';
import LoggerFactory from 'utilities/loggerv2/loggerFactory';
import ErrorCodes from 'utilities/loggerv2/errorCodes';
import { startSpan, endSpan, failSpan } from 'utilities/loggerv2/performanceSpans';
import vdpDeliveryContent from 'utilities/content/vdpDeliveryContent';
import { DeliveryAndReturnsProps } from './DeliveryAndReturnsProps';
import DeliveryAndReturnsModal from './Dialog/DeliveryAndReturnsDialog';
import DeliveryCard from './Card/DeliveryCard';
import Skeleton from './Skeleton/Skeleton';
import ErrorCodeDefinition from '../../utilities/loggerv2/errorCodeDefinition';

const loggerVDP = LoggerFactory.getLogger('DeliveryAndReturnsVDP');

const DEFAULT_CONTENT: DeliveryAndReturnContentItem = {
  pageText: 'Please select an option to view delivery options on this item',
  pageLink: '',
  modalHeading: '',
  modalBody: '',
  title: 'Delivery and returns',
};

const ONE_PERSON = 'OnePerson';

function DeliveryAndReturns(props: DeliveryAndReturnsProps) {
  const { brand, skus, csrfToken, vdpMockEnabled, mockResponseName, site } = props;
  const { selectedSku } = useSelectedSkuContext();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogRendered, setDialogRendered] = useState(false);
  const isFirstRender = useRef(true);
  const selectedContentKey = selectedSku?.deliveryAndReturnContentKey;
  const [vdpLoading, setVDPLoading] = useState(true);
  const [VDP, setVDP] = useState<any>();
  const [deliveryContentMap, setDeliveryContentMap] = useState<Record<string, DeliveryAndReturnContentItem>>({});
  const defaultSku = skus[0];

  const vdpService = new VDPService(vdpMockEnabled);

  const ONE_PERSON_DELIVERY_CARD_TITLE = site === 'veryie' ? 'Standard delivery by An Post' : 'FREE Delivery Options';

  const brandLogoUrl =
    brand && selectedContentKey === DeliveryAndReturnContentKey.FFM ? getBrandLogoUrl(brand) : undefined;

  const sendAnalyticsEvent = (action: 'Open' | 'Closed') => {
    if (selectedContentKey) {
      analyticsEvent({
        gaLabel: `Delivery and Returns - ${
          (selectedSku ?? defaultSku)
            ? deliveryContentMap[(selectedSku ?? defaultSku)?.option ?? '']?.pageText
            : DEFAULT_CONTENT.pageText
        } - ${
          (selectedSku ?? defaultSku)
            ? deliveryContentMap[(selectedSku ?? defaultSku)?.option ?? '']?.pageLink
            : DEFAULT_CONTENT.pageLink
        } - ${action}`,
      });
    }
  };

  const closeDialog = () => {
    setDialogOpen(false);
    sendAnalyticsEvent('Closed');
  };

  const openDialog = () => {
    if (dialogRendered) {
      setDialogOpen(true);
    } else {
      setDialogRendered(true);
    }

    sendAnalyticsEvent('Open');
  };

  const getVDPRequestSkus = () => {
    const requestSkus = skus.map((sku) => ({
      sku: sku.option,
      productTypeCode: sku.productTypeCode || 1,
    }));

    return requestSkus;
  };

  const logAndFailSpan = async (spanId: string, message: string, code: ErrorCodeDefinition, stack = '') => {
    const error = new Error(message);
    Object.defineProperty(error, 'code', { value: code, writable: true, enumerable: true });

    (await loggerVDP).logWithCode(code, error);

    failSpan(spanId, {
      errorType: code,
      message,
      stack,
    });
  };

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (dialogRendered) {
      setDialogOpen(true);
    }
  }, [dialogRendered]);

  useEffect(() => {
    if (!vdpLoading && VDP) {
      const newEntries: Record<string, DeliveryAndReturnContentItem> = {};

      skus.forEach((sku) => {
        if (sku.option && !(sku.option in deliveryContentMap)) {
          const vdpContent = vdpDeliveryContent(sku.deliveryAndReturnContentKey, sku.option, VDP, site, brand);

          if (vdpContent && typeof vdpContent === 'object') {
            newEntries[sku.option] = vdpContent as DeliveryAndReturnContentItem;
          }
        }
      });

      if (Object.keys(newEntries).length > 0) {
        setDeliveryContentMap((prev) => ({ ...prev, ...newEntries }));
      }
    }
  }, [vdpLoading, VDP]);

  useEffect(() => {
    if (!csrfToken && !vdpMockEnabled) return;

    const getVDP = async () => {
      const skusPayload = getVDPRequestSkus();

      const spanId = startSpan('getVDP', {
        skus: skusPayload,
        csrfTokenProvided: true,
        vdpMockEnabled,
      });

      try {
        const response = await vdpService.getVDP({ skus: skusPayload }, csrfToken, {
          vdpMockEnabled,
          mockResponseName,
          site: site || 'very',
        });

        if (!response) {
          await logAndFailSpan(spanId, 'VDP API returned an empty response.', ErrorCodes.API_INVALID_RESPONSE);
          return;
        }

        setVDP(response);
        endSpan(spanId, { success: true });
      } catch (error) {
        const message = error instanceof Error ? error.message : String(error);
        await logAndFailSpan(spanId, message, ErrorCodes.API_REQUEST_FAILED, error?.stack);
      } finally {
        setVDPLoading(false);
      }
    };

    getVDP();
  }, [csrfToken]);

  if (vdpLoading) {
    return <Skeleton />;
  }

  if (!VDP || ((selectedSku ?? defaultSku) && !(selectedSku ?? defaultSku).deliveryAndReturnContentKey)) return null;
  return (
    <>
      <DeliveryCard
        onClick={openDialog}
        text={deliveryContentMap[(selectedSku ?? defaultSku)?.option ?? '']?.pageText}
        link={deliveryContentMap[(selectedSku ?? defaultSku)?.option ?? '']?.pageLink}
        title={
          (selectedSku ?? defaultSku)?.deliveryAndReturnContentKey === ONE_PERSON
            ? ONE_PERSON_DELIVERY_CARD_TITLE
            : DEFAULT_CONTENT.title
        }
      />
      {dialogRendered && (
        <DeliveryAndReturnsModal
          body={deliveryContentMap[(selectedSku ?? defaultSku)?.option ?? '']?.modalBody}
          brand={brand}
          brandLogoUrl={brandLogoUrl}
          heading={deliveryContentMap[(selectedSku ?? defaultSku)?.option ?? '']?.modalHeading}
          onClose={closeDialog}
          open={dialogOpen}
        />
      )}
    </>
  );
}

export default DeliveryAndReturns;
